<template>
  <aside
    id="sidenav-main"
    class="sidenav-container modern-sidebar"
    :class="{ 'is-collapsed': isCollapsed, 'is-rtl': isRTL, 'dark-mode': isDarkMode }"
  >
    <div class="sidenav-header">
      <router-link to="/" class="logo-link">
        <div class="logo-icon-wrapper">
          <el-icon :size="28"><School /></el-icon>
        </div>
        <transition name="fade">
          <span v-if="!isCollapsed" class="logo-text">Caledonian</span>
        </transition>
      </router-link>
    </div>

    <div class="sidenav-content" ref="sidenavContent">
      <sidenav-list :collapsed="isCollapsed" />
    </div>

    <div class="sidenav-footer">
       <div class="toggle-button" @click="toggleCollapse" :title="isCollapsed ? 'إظهار القائمة' : 'إخفاء القائمة'">
          <el-icon :size="20">
            <component :is="isCollapsed ? 'Expand' : 'Fold'" />
          </el-icon>
          <transition name="fade">
            <span v-if="!isCollapsed" class="toggle-text">طي القائمة</span>
          </transition>
       </div>
       <div class="logout-button" @click="handleLogout" :title="'تسجيل الخروج'">
          <el-icon :size="20"><SwitchButton /></el-icon>
          <transition name="fade">
            <span v-if="!isCollapsed" class="toggle-text">تسجيل الخروج</span>
          </transition>
       </div>
    </div>
  </aside>
</template>

<script>
import SidenavList from "./SidenavList.vue";
import { mapState, mapMutations, mapGetters } from "vuex";
import { School, Expand, Fold, SwitchButton } from '@element-plus/icons-vue';
import axios from 'axios';

export default {
  name: "SidebarIndex",
  components: {
    SidenavList,
    School,
    Expand,
    Fold,
    SwitchButton,
  },
  data() {
    return {
      isCollapsed: false,
    };
  },
  computed: {
    ...mapState(["isRTL"]),
    ...mapGetters(['isDarkMode']),
  },
  methods: {
    ...mapMutations(['LOGOUT']),
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      // Save state to localStorage for persistence
      localStorage.setItem('sidenavCollapsed', this.isCollapsed);
    },
    async handleLogout() {
      try {
        await axios.post('https://cale.caledonian.ly/api/logout', {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_type');
        localStorage.removeItem('user_id');
        localStorage.removeItem('username');
        this.LOGOUT();
        this.$router.push({ name: 'SignIn' });
      } catch (error) {
        console.error('Logout error:', error);
        // Even if API call fails, clear local storage and logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_type');
        localStorage.removeItem('user_id');
        localStorage.removeItem('username');
        this.LOGOUT();
        this.$router.push({ name: 'SignIn' });
      }
    }
  },
  mounted() {
    // Restore collapsed state from localStorage
    const savedState = localStorage.getItem('sidenavCollapsed');
    if (savedState !== null) {
      this.isCollapsed = savedState === 'true';
    }
  },
};
</script>

<style scoped>
.modern-sidebar {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 280px;
  position: fixed;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, #1a1f35 0%, #0f1419 100%);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.modern-sidebar.is-rtl {
  left: auto;
  right: 0;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  border-right: none;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.modern-sidebar.is-collapsed {
  width: 80px;
}

.modern-sidebar.dark-mode {
  background: linear-gradient(180deg, #0a0e14 0%, #050709 100%);
  border-right-color: rgba(255, 255, 255, 0.05);
}

.modern-sidebar.dark-mode.is-rtl {
  border-left-color: rgba(255, 255, 255, 0.05);
}

.sidenav-header {
  padding: 20px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 72px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 14px;
  text-decoration: none;
  color: #ffffff;
  width: 100%;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: translateX(-2px);
}

.logo-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.logo-icon-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.logo-text {
  font-size: 1.35rem;
  font-weight: 700;
  white-space: nowrap;
  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.5px;
}

.sidenav-content {
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.sidenav-content::-webkit-scrollbar {
  width: 6px;
}

.sidenav-content::-webkit-scrollbar-track {
  background: transparent;
}

.sidenav-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  transition: background 0.3s ease;
}

.sidenav-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.25);
}

.sidenav-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
}

.toggle-button,
.logout-button {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #a6adb4;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  font-size: 0.9rem;
  position: relative;
  overflow: hidden;
}

.toggle-button::before,
.logout-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.toggle-button:hover::before,
.logout-button:hover::before {
  left: 100%;
}

.toggle-button:hover {
  background: rgba(102, 126, 234, 0.15);
  color: #ffffff;
  transform: translateX(-2px);
}

.logout-button {
  margin-top: 10px;
}

.logout-button:hover {
  background: rgba(245, 108, 108, 0.15);
  color: #f56c6c;
  transform: translateX(-2px);
}

.toggle-text {
  font-weight: 500;
  white-space: nowrap;
  transition: opacity 0.2s ease;
}

/* Fade transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modern-sidebar:not(.is-collapsed) {
    width: 100%;
    max-width: 280px;
  }
}
</style>
